import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { HostingsAPI, ComplementsAPI } from '../api'
import { Trash2, RefreshCw, X, AlertTriangle, CheckCircle, Image as ImageIcon, Wand2 } from 'lucide-react'

interface BlobItem {
  url: string
  pathname: string
  size: number
  uploadedAt: string
}

interface FileUsage {
  type: 'HOSTING' | 'COMPLEMENT'
  id: string
  field: 'mainPhotoUrl' | 'photos'
  slug: string
  index?: number
  entityName: string
  data: any
}

interface MediaManagerProps {
  onClose?: () => void
  onSelect?: (url: string) => void
}

export const MediaManager = ({ onClose, onSelect }: MediaManagerProps) => {
  const { getToken } = useAuth()
  const [blobs, setBlobs] = useState<BlobItem[]>([])
  const [usedUrls, setUsedUrls] = useState<Set<string>>(new Set())
  const [fileUsage, setFileUsage] = useState<Map<string, FileUsage>>(new Map())
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [filter, setFilter] = useState<'ALL' | 'UNUSED' | 'FIXABLE'>('UNUSED')

  const loadData = async () => {
    setLoading(true)
    try {
      // 1. Fetch all blobs
      const token = await getToken()
      const resBlobs = await fetch('/api/list-files', {
        headers: { Authorization: `Bearer ${token}` }
      })
      const dataBlobs = await resBlobs.json()
      if (dataBlobs.blobs) {
        setBlobs(dataBlobs.blobs)
      }

      // 2. Fetch all hostings to check usage
      const resHostings = await HostingsAPI.list()
      const hostings = resHostings.data || []

      // 3. Fetch all complements to check usage
      const resComplements = await ComplementsAPI.list()
      const complements = resComplements.data || []

      const used = new Set<string>()
      const usage = new Map<string, FileUsage>()

      hostings.forEach((h: any) => {
        const slug = (h.name || 'hosting').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        
        if (h.mainPhotoUrl) {
          used.add(h.mainPhotoUrl)
          usage.set(h.mainPhotoUrl, { type: 'HOSTING', id: h.id, field: 'mainPhotoUrl', slug, entityName: h.displayName, data: h })
        }
        if (Array.isArray(h.photos)) {
          h.photos.forEach((p: string, idx: number) => {
            used.add(p)
            usage.set(p, { type: 'HOSTING', id: h.id, field: 'photos', slug, index: idx, entityName: h.displayName, data: h })
          })
        }
      })

      complements.forEach((c: any) => {
        const slug = (c.name || 'complement').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        
        if (c.mainPhotoUrl) {
          used.add(c.mainPhotoUrl)
          usage.set(c.mainPhotoUrl, { type: 'COMPLEMENT', id: c.id, field: 'mainPhotoUrl', slug, entityName: c.displayName, data: c })
        }
        if (Array.isArray(c.photos)) {
          c.photos.forEach((p: string, idx: number) => {
            used.add(p)
            usage.set(p, { type: 'COMPLEMENT', id: c.id, field: 'photos', slug, index: idx, entityName: c.displayName, data: c })
          })
        }
      })
      
      setUsedUrls(used)
      setFileUsage(usage)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleRename = async (blob: BlobItem) => {
    const usage = fileUsage.get(blob.url)
    if (!usage) return

    if (!confirm(`¿Renombrar imagen para ${usage.entityName}?`)) return

    setRenaming(blob.url)
    try {
      const token = await getToken()
      
      // Calculate new filename
      const ext = blob.pathname.split('.').pop() || 'bin'
      let suffix = ''
      if (usage.field === 'mainPhotoUrl') {
        suffix = 'featured'
      } else {
        suffix = `gallery-${usage.index}`
      }
      // Add timestamp to avoid caching issues and collisions
      const newFilename = `hostings/${usage.slug}/${usage.slug}-${suffix}-${Date.now()}.${ext}`

      // 1. Call rename API
      const res = await fetch('/api/rename-file', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oldUrl: blob.url,
          newPath: newFilename
        })
      })
      
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error renaming file')
      
      const newUrl = data.url

      // 2. Update Database
      let updateRes
      if (usage.type === 'HOSTING') {
        // Fetch fresh data to ensure we don't overwrite other changes
        const freshRes = await HostingsAPI.get(usage.id)
        if (!freshRes.success || !freshRes.data) throw new Error('Hosting not found')
        const freshData = freshRes.data

        const updateData: any = {}
        if (usage.field === 'mainPhotoUrl') {
          updateData.mainPhotoUrl = newUrl
        } else if (usage.field === 'photos') {
          const photos = [...(freshData.photos || [])]
          // Find the index of the old URL in the fresh data
          const index = photos.indexOf(blob.url)
          if (index !== -1) {
            photos[index] = newUrl
            updateData.photos = photos
          } else {
            // Fallback to usage.index if the URL matches (double check)
            if (typeof usage.index === 'number' && photos[usage.index] === blob.url) {
               photos[usage.index] = newUrl
               updateData.photos = photos
            } else {
               console.warn('Could not find photo to update in hosting', blob.url)
               // If we can't find it, we can't update it safely. 
               // However, we should still proceed to update local state if we want to reflect the file rename?
               // No, if DB update fails, we should probably throw or alert.
               // But maybe the file was already renamed in DB?
            }
          }
        }
        
        if (Object.keys(updateData).length > 0) {
            updateRes = await HostingsAPI.update(usage.id, updateData)
        } else {
            updateRes = { success: true, data: freshData } // No changes needed
        }

      } else {
        // Fetch fresh data for complement
        const freshRes = await ComplementsAPI.get(usage.id)
        if (!freshRes.success || !freshRes.data) throw new Error('Complement not found')
        const freshData = freshRes.data

        const updateData: any = {}
        if (usage.field === 'mainPhotoUrl') {
          updateData.mainPhotoUrl = newUrl
        } else if (usage.field === 'photos') {
          const photos = [...(freshData.photos || [])]
          const index = photos.indexOf(blob.url)
          if (index !== -1) {
            photos[index] = newUrl
            updateData.photos = photos
          } else {
             if (typeof usage.index === 'number' && photos[usage.index] === blob.url) {
               photos[usage.index] = newUrl
               updateData.photos = photos
            } else {
               console.warn('Could not find photo to update in complement', blob.url)
            }
          }
        }

        if (Object.keys(updateData).length > 0) {
            updateRes = await ComplementsAPI.update(usage.id, updateData)
        } else {
            updateRes = { success: true, data: freshData }
        }
      }

      if (updateRes.success) {
        // 3. Update local state
        const newData = updateRes.data
        setBlobs(prev => prev.map(b => b.url === blob.url ? { ...b, url: newUrl, pathname: data.pathname } : b))
        
        setFileUsage(prev => {
          const next = new Map(prev)
          next.delete(blob.url)
          
          // Update the renamed entry
          next.set(newUrl, { ...usage, data: newData }) 
          
          // Update ALL other entries for this entity with the new data to prevent stale references
          for (const [key, value] of next.entries()) {
            if (value.type === usage.type && value.id === usage.id) {
               // Update the data reference
               // We also need to update the index if the photos array changed? 
               // The photos array index shouldn't change for other photos if we just renamed one.
               // But if we reloaded data, we should probably be careful.
               // For now, just updating data reference is better than nothing.
               next.set(key, { ...value, data: newData })
            }
          }
          return next
        })
        
        setUsedUrls(prev => {
          const next = new Set(prev)
          next.delete(blob.url)
          next.add(newUrl)
          return next
        })
      } else {
        throw new Error(updateRes.error || 'Error updating database')
      }

    } catch (err: any) {
      console.error(err)
      alert('Error: ' + err.message)
    } finally {
      setRenaming(null)
    }
  }

  const handleDelete = async (url: string) => {
    if (!confirm('¿Estás seguro de eliminar este archivo permanentemente?')) return

    setDeleting(url)
    try {
      const token = await getToken()
      const res = await fetch('/api/delete-file', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url })
      })
      if (res.ok) {
        setBlobs(prev => prev.filter(b => b.url !== url))
      }
    } catch (err) {
      console.error(err)
    } finally {
      setDeleting(null)
    }
  }

  const isFixable = (blob: BlobItem) => {
    const usage = fileUsage.get(blob.url)
    if (!usage) return false
    
    // Check if filename starts with slug
    const filename = blob.pathname.split('/').pop() || ''
    return !filename.startsWith(usage.slug + '-')
  }

  const filteredBlobs = useMemo(() => {
    if (filter === 'ALL') return blobs
    if (filter === 'FIXABLE') return blobs.filter(b => isFixable(b))
    return blobs.filter(b => !usedUrls.has(b.url))
  }, [blobs, usedUrls, filter, fileUsage])


  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const containerClass = onClose
    ? "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    : "w-full max-w-7xl mx-auto p-6 mt-24"

  const cardClass = onClose
    ? "bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col dark:bg-slate-800 dark:border dark:border-slate-700"
    : "bg-white rounded-xl shadow-sm border border-gray-200 w-full flex flex-col h-[calc(100vh-150px)] dark:bg-slate-800 dark:border-slate-700"

  return (
    <div className={containerClass}>
      <div className={cardClass}>
        <div className="p-4 border-b flex items-center justify-between dark:border-slate-700">
          <h3 className="text-xl font-bold flex items-center gap-2 dark:text-white">
            <ImageIcon size={20} />
            Gestor de Archivos
          </h3>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full dark:text-gray-400 dark:hover:bg-slate-700">
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-4 border-b bg-gray-50 flex items-center justify-between flex-wrap gap-4 dark:bg-slate-700/30 dark:border-slate-700">
          <div className="flex items-center gap-2 bg-white p-1 rounded-lg border dark:bg-slate-800 dark:border-slate-700">
            <button
              onClick={() => setFilter('UNUSED')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'UNUSED' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-slate-700'}`}
            >
              Sin usar ({blobs.filter(b => !usedUrls.has(b.url)).length})
            </button>
            <button
              onClick={() => setFilter('ALL')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'ALL' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-slate-700'}`}
            >
              Todos ({blobs.length})
            </button>
            <button
              onClick={() => setFilter('FIXABLE')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === 'FIXABLE' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-slate-700'}`}
            >
              Corregir Nombre ({blobs.filter(b => isFixable(b)).length})
             </button>
          </div>
          <button onClick={loadData} disabled={loading} className="p-2 hover:bg-gray-200 rounded-full text-gray-600 dark:text-gray-400 dark:hover:bg-slate-700" title="Recargar">
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 dark:bg-slate-900/30">
          {loading && blobs.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="animate-spin text-blue-500 mr-2" /> <span className="dark:text-gray-400">Cargando...</span>
            </div>
          ) : filteredBlobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500 dark:text-gray-400">
              <CheckCircle size={48} className="mb-2 text-green-500" />
              <p>No hay archivos {filter === 'UNUSED' ? 'sin usar' : ''}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredBlobs.map(blob => {
                const isUsed = usedUrls.has(blob.url)
                const canFix = isFixable(blob)
                return (
                  <div key={blob.url} className={`group relative border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700 ${!isUsed ? 'border-red-200 ring-1 ring-red-100 dark:border-red-900/50 dark:ring-red-900/30' : canFix ? 'border-amber-200 ring-1 ring-amber-100 dark:border-amber-900/50 dark:ring-amber-900/30' : ''}`}>
                    <div className="aspect-square bg-gray-100 relative dark:bg-slate-900">
                      <img src={blob.url} alt="file" className="w-full h-full object-cover" loading="lazy" />
                      {!isUsed && (
                        <div className="absolute top-1 left-1 bg-red-100 text-red-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-1 dark:bg-red-900/80 dark:text-red-200 dark:border-red-800">
                          <AlertTriangle size={10} /> Sin uso
                        </div>
                      )}
                      {canFix && isUsed && (
                        <button
                          onClick={() => handleRename(blob)}
                          disabled={renaming === blob.url}
                          className="absolute top-1 right-1 bg-amber-100 text-amber-700 p-1.5 rounded-full border border-amber-200 shadow-sm hover:bg-amber-200 dark:bg-amber-900/80 dark:text-amber-200 dark:border-amber-800 dark:hover:bg-amber-900"
                          title={`Renombrar a: ${fileUsage.get(blob.url)?.slug}-...`}
                        >
                          {renaming === blob.url ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />}
                        </button>
                      )}
                    </div>
                    <div className="p-2 text-xs text-gray-600 dark:text-gray-300">
                      <div className="truncate font-medium" title={blob.pathname}>{blob.pathname.split('/').pop()}</div>
                      <div className="flex justify-between mt-1 text-gray-400 dark:text-gray-500">
                        <span>{formatSize(blob.size)}</span>
                        <span>{new Date(blob.uploadedAt).toLocaleDateString()}</span>
                      </div>
                      {onSelect && (
                        <button
                          onClick={() => onSelect(blob.url)}
                          className="mt-2 w-full bg-blue-50 text-blue-600 hover:bg-blue-100 py-1 rounded text-center font-medium border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/50"
                        >
                          Seleccionar
                        </button>
                      )}
                    </div>
                    {!isUsed && (
                      <button
                        onClick={() => handleDelete(blob.url)}
                        disabled={deleting === blob.url}
                        className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full text-red-600 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 border shadow-sm dark:bg-slate-800/90 dark:text-red-400 dark:border-slate-600 dark:hover:bg-red-900/50"
                        title="Eliminar permanentemente"
                      >
                        {deleting === blob.url ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
